"use client";

import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import { Offer, ProductData } from "@/services/dataTypes";
import OfferDuration from "./OfferDuration";
import { getBaseImageUrl } from "@/constants/hooks";
import Link from "next/link";
import { faCheck, faCopy, faTimes } from "@/constants/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Props {
  data: Offer | ProductData;
  onClose: () => void;
  domain: string;
  merchantHref: string;
  finalDiscountTag?: string | null;
  merchantImg?: string | null;
  productImg?: string | null;
  imageSrc?: string | null; // direct pre-computed URL — e.g. from OfferDetailsToggle
}

const SimpleOfferModal = ({
  data,
  onClose,
  merchantImg,
  productImg,
  imageSrc: imageSrcProp,
  domain,
  merchantHref,
  finalDiscountTag,
}: Props) => {
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);

  // Resolve image with same priority order as Theme-4:
  // 1. Pre-computed full URL from caller (most reliable)
  // 2. Explicit productImg raw path
  // 3. Explicit merchantImg raw path
  // 4. Derive from data object
  const imageSrc = useMemo(() => {
    if (imageSrcProp) return imageSrcProp;
    if (productImg) return getBaseImageUrl(domain, productImg, "");
    if (merchantImg) return getBaseImageUrl(domain, merchantImg, "");
    const type = (data as any)?.offer_type?.name;
    if (type === "product" && (data as ProductData)?.product_image)
      return getBaseImageUrl(domain, (data as ProductData).product_image, "");
    if ((data as Offer)?.merchant?.merchant_logo)
      return getBaseImageUrl(domain, (data as Offer).merchant.merchant_logo, "");
    return "";
  }, [data, domain, merchantImg, productImg, imageSrcProp]);

  useEffect(() => {
    setMounted(true);
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  const handleCopy = async () => {
    const codeToCopy = data?.coupon_code;
    if (!codeToCopy) return;

    try {
      if (navigator?.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(codeToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = codeToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy promo code: ", err);
    }
  };

  if (!mounted) return null;

  const finalUrl = data?.url?.startsWith("/")
    ? data?.url.replace(/^\/+/, "")
    : data?.url;
  const absoluteOutUrl = `/${finalUrl}`;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
      {/* Dimmed Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Main Split Card Container with Fixed Outer Bounds */}
      <div className="relative w-full max-w-[680px] h-[85vh] max-h-[560px] bg-[#FFFDE0] rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-[#800000]/15 overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3.5 top-3.5 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-[#800000] text-[#800000] hover:text-white transition-all duration-200 border border-[#800000]/10 shadow-sm active:scale-90"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>

        {/* Grid Wrapper */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-0 overflow-hidden">
          
          {/* LEFT COLUMN: FIXED POSITION & PERFECTLY BALANCED SPACING */}
          <div className="md:col-span-5 bg-white/50 p-4 sm:p-5 flex flex-col justify-between items-center border-b md:border-b-0 md:border-r border-[#800000]/10 relative shrink-0 overflow-y-auto md:overflow-hidden">
            
            {/* Top Badges */}
            <div className="w-full flex items-center justify-between shrink-0">
              {finalDiscountTag ? (
                <span className="px-3 py-1 rounded-full bg-[#800000] text-[#FFFDE0] text-[10px] font-bold uppercase tracking-wider shadow-sm">
                  {finalDiscountTag}
                </span>
              ) : (
                <span />
              )}

              <div className="px-3 py-1">
                <OfferDuration endDate={data?.end_date} className="scale-90" />
              </div>
            </div>

            {/* Product Image Frame - Seamless Fitting without extra gaps */}
            <div className="relative w-full flex-1 my-3 min-h-[170px] max-h-[250px] bg-white rounded-2xl p-2.5 border border-[#800000]/10 shadow-xs flex items-center justify-center overflow-hidden">
              {imageSrc ? (
                <div className="relative w-full h-full">
                  <Image
                    src={imageSrc}
                    alt={data?.offer_title || "Offer"}
                    fill
                    className="object-contain"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-[#800000]/20">
                  <svg className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 19.5h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Promo Code & Secure Discount CTA */}
            <div className="w-full space-y-2 shrink-0">
              {data?.coupon_code && (
                <div 
                  onClick={handleCopy}
                  className="bg-white border border-[#800000]/20 hover:border-[#800000]/40 rounded-xl p-2 pl-3 flex items-center justify-between gap-2 shadow-xs cursor-pointer transition-all active:scale-[0.99]"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] font-bold text-[#800000]/60 uppercase tracking-wider">
                      Promo Code
                    </span>
                    <span className="font-mono text-sm font-black tracking-wider text-[#1A1A1A] truncate select-all">
                      {data.coupon_code}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all duration-200 shrink-0 ${
                      copied
                        ? "bg-emerald-600 text-white"
                        : "bg-[#800000] text-white hover:bg-black"
                    }`}
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="text-xs" />
                    <span>{copied ? "Copied" : "Copy"}</span>
                  </button>
                </div>
              )}

              {/* Secure Discount Button */}
              <Link
                href={absoluteOutUrl}
                target="_blank"
                className="flex h-11 w-full items-center justify-center rounded-xl bg-[#800000] text-[#FFFDE0] font-bold text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-black hover:shadow-md active:scale-[0.98]"
              >
                {data?.coupon_code ? "Shop with Coupon" : "Secure Discount"}
              </Link>
            </div>

          </div>

          {/* RIGHT COLUMN: INDEPENDENTLY SCROLLABLE PANEL */}
          <div className="md:col-span-7 p-6 sm:p-7 flex flex-col space-y-5 overflow-y-auto custom-modal-scrollbar h-full">
            
            {/* Header Title */}
            <div className="space-y-1.5 shrink-0">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#800000]/70">
                Verified Deal
              </p>

              <h3 className="text-lg sm:text-xl font-bold text-[#2A1515] leading-snug tracking-tight">
                {data.offer_title}
              </h3>
            </div>

            {/* Offer Details Heading & Full Description */}
            {data?.offer_detail && (
              <div className="pt-3 border-t border-[#800000]/10 flex-1">
                <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-[#800000] mb-2">
                  Offer Details
                </h4>
                
                <div
                  className="text-[13px] text-slate-600 leading-relaxed offer-description-html whitespace-normal break-words"
                  dangerouslySetInnerHTML={{ __html: data?.offer_detail }}
                />
              </div>
            )}

          </div>

        </div>

        {/* Footer Bar */}
        <div className="bg-white/60 border-t border-[#800000]/10 py-2.5 text-center shrink-0 z-10">
          <p className="text-[9px] font-bold text-[#800000]/50 uppercase tracking-[0.2em]">
            Verified Exclusive Offer • 2026
          </p>
        </div>
      </div>

      {/* Global Style Adjustments & CSS Override */}
      <style jsx global>{`
        .custom-modal-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-modal-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-modal-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(128, 0, 0, 0.2);
          border-radius: 10px;
        }
        .custom-modal-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(128, 0, 0, 0.4);
        }

        .offer-description-html,
        .offer-description-html * {
          overflow: visible !important;
          text-overflow: clip !important;
          white-space: normal !important;
          -webkit-line-clamp: none !important;
          -webkit-box-orient: horizontal !important;
          line-clamp: none !important;
          max-height: none !important;
        }

        .offer-description-html p {
          margin-bottom: 8px;
        }
        .offer-description-html p:last-child {
          margin-bottom: 0;
        }
        .offer-description-html ul,
        .offer-description-html ol {
          margin-left: 16px;
          margin-bottom: 8px;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SimpleOfferModal;